import { ApiService } from 'lib/services/api.service';

import { duration } from 'lib/tools';
import { countriesByNameLower, isCountryCode } from 'lib/lists/countries';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import { Subject } from 'rxjs';

const scriptName = 'easypost.class';

interface ShippingQuoteParams {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    items: any[];
}

export class EasyPostShipping {
    declare private readonly deps: { apiService: ApiService };

    /**
     * Last successful quote, keyed by every input that affects the EasyPost
     * rate. The server re-quotes with the same inputs when the order is
     * created, so anything missing here (address2 was, see ST-3874) can make
     * the two rates differ.
     */
    private previous: { key?: any; res?: any } = {};

    isRetrieving: boolean = false;
    retrieving$: Subject<boolean> = new Subject<boolean>();

    constructor(dependencies: any) {
        try {
            // Non-enumerable: keeps the per-request services out of serialization and comparisons.
            Object.defineProperty(this, 'deps', { value: dependencies, enumerable: false });
        } catch (err) {
            console.error(...new ExxComError(620394, scriptName, err).stamp());
        }
    }

    async get(params: ShippingQuoteParams) {
        let started = false;
        try {
            if (this.isPreviousSame(params)) {
                return this.previous.res;
            }
            const { address1, address2, city, state, zip, country, items } = params;
            const key = this.getKey(params);
            this.previous = {};
            started = true;
            this.isRetrieving = true;
            this.retrieving$.next(true);
            const countryCode = isCountryCode(country) ? country : get(countriesByNameLower[country.toLowerCase()], 'code');
            duration.start();
            const res: any = await this.request({
                address1,
                address2: address2 || '',
                city,
                state,
                zip,
                country: countryCode,
                items,
            });
            // Only cache successes so a failed quote is retried next time
            this.previous = res.success ? { key, res } : {};
            return res;
        } catch (err) {
            this.previous = {};
            console.error(...new ExxComError(520394, scriptName, err).stamp());
            return { success: false };
        } finally {
            if (started) {
                this.isRetrieving = false;
                this.retrieving$.next(false);
            }
        }
    }

    isPreviousSame(params: ShippingQuoteParams) {
        try {
            return !!this.previous.res && isEqual(this.previous.key, this.getKey(params));
        } catch (err) {
            console.error(...new ExxComError(523093, scriptName, err).stamp());
            return false;
        }
    }

    /**
     * Snapshot of the quote inputs. Items are copied (not referenced) so
     * in-place cart changes can't make a stale quote look current, and the
     * comparison is two-way so added or removed items are detected.
     */
    private getKey({ address1, address2, city, state, zip, country, items }: ShippingQuoteParams) {
        return {
            address1,
            address2: address2 || '',
            city,
            state,
            zip,
            country,
            items: (items || []).map((item: any) => ({
                product: get(item, 'product._id') || get(item, 'product'),
                quantity: get(item, 'quantity'),
                weight: get(item, 'product.weight'),
                length: get(item, 'product.length'),
                width: get(item, 'product.width'),
                height: get(item, 'product.height'),
                freeShipping: get(item, 'product.freeShipping'),
                leadTime: get(item, 'product.leadTime'),
            })),
        };
    }

    private async request({ address1, address2, city, state, zip, country, items }) {
        try {
            const req = { address1, address2, city, state, zip, country, items };
            duration.start();
            const res = await this.deps.apiService.post('easypost/shipping', req);
            if (!res.success || res.name == 'HttpErrorResponse') {
                res.success = false;
            }
            res.duration = duration.get();
            return res;
        } catch (err) {
            console.error(...new ExxComError(520394, scriptName, err).stamp());
            return { success: false };
        }
    }
}
